import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Typography,
  Avatar,
  Box,
  Stack,
  Chip,
} from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import PlaceIcon from '@mui/icons-material/Place';
import { LikeButton } from './LikeButton';
import type { FeedItem } from '../types';

export interface FeedItemCardProps {
  item: FeedItem;
  onUserClick?: (userId: string) => void;
  onItemClick?: (item: FeedItem) => void;
}

/**
 * Feed item card showing a trip or pin with user avatar, title, likes, and comments count.
 */
export function FeedItemCard({ item, onUserClick, onItemClick }: FeedItemCardProps) {
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      {item.thumbnailUrl && (
        <CardMedia
          component="img"
          height="200"
          image={item.thumbnailUrl}
          alt={item.title}
          sx={{ cursor: 'pointer' }}
          onClick={() => onItemClick?.(item)}
        />
      )}
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Avatar
            src={item.user.avatarUrl}
            alt={item.user.displayName}
            sx={{ width: 36, height: 36, cursor: 'pointer' }}
            onClick={() => onUserClick?.(item.user.id)}
          />
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ cursor: 'pointer' }}
              onClick={() => onUserClick?.(item.user.id)}
            >
              {item.user.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(item.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
          <Chip label={item.type === 'trip' ? 'Trip' : 'Pin'} size="small" variant="outlined" sx={{ ml: 'auto' }} />
        </Stack>

        <Typography
          variant="h6"
          sx={{ cursor: 'pointer', fontSize: '1rem' }}
          onClick={() => onItemClick?.(item)}
        >
          {item.title}
        </Typography>

        {item.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
            {item.description}
          </Typography>
        )}

        {item.location && (item.location.city || item.location.countryCode) && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            <PlaceIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {[item.location.city, item.location.countryCode].filter(Boolean).join(', ')}
            </Typography>
          </Stack>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 1.5 }}>
        <LikeButton
          targetType={item.type}
          targetId={item.type === 'trip' ? item.tripId! : item.pinId!}
          initialIsLiked={item.isLiked}
          initialCount={item.likesCount}
        />
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 2 }}>
          <CommentIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {item.commentsCount}
          </Typography>
        </Stack>
      </CardActions>
    </Card>
  );
}
